/**
 * Brand Kit, Brand Voice, Client Overview, and Knowledge Base —
 * Learn-Mode inline tips.
 */

import type { LearnTip } from "./types";

export const brandTips: LearnTip[] = [
  {
    key: "social-brand-kit",
    title: "Brand Kit",
    body: "Your brand colors, fonts, and style guide. Templates and the design editor pull from this automatically, so the more complete it is, the more on-brand every design looks without extra manual work.",
  },
  {
    key: "brand-kit-update-designs",
    title: "Update Existing Designs",
    body: "Re-applies this brand kit's current colors, fonts, and logos to every design that was built from it — useful after you change the kit so older designs catch up. Headlines and body text stay exactly as written; only the brand elements update.",
  },
  {
    key: "social-brand-kit-completeness",
    title: "Kit Completeness",
    body: "A score for how much of the brand kit is filled in. Templates fill in missing brand details automatically, but the more complete the kit, the better they look without manual touch-ups.",
  },
  {
    key: "brand-refresh-dialog",
    title: "Updating Designs to the Latest Brand",
    body: "Applies this client's current brand kit to every design built from it. Colors, fonts, and logos update on every page; your headlines and text stay exactly as written.",
  },
  {
    key: "brand-refresh-preview",
    title: "What Will Change",
    body: "A preview of exactly which designs will update and which are already current, before you commit. If there are more matching designs than shown, run it again afterward to catch the rest.",
  },
  {
    key: "brand-refresh-results",
    title: "Update Results",
    body: "What happened when the brand refresh ran — how many designs updated, were already current, were skipped, or failed. Each updated design keeps a “Before brand refresh” version you can restore if you change your mind.",
  },
  {
    key: "social-brand-voice",
    title: "Brand Voice",
    body: "Teaches the AI how this client sounds, so every AI-written caption, chat reply, and design follows the same tone automatically.",
  },
  {
    key: "social-brand-voice-rules",
    title: "Voice Rules",
    body: "The specific tone and wording rules the AI copywriter follows for this client on every caption, chat reply, and design. Re-analyzing past posts refines these without losing the rules you've already saved.",
  },
  {
    key: "social-brand-voice-preview",
    title: "Try the Voice",
    body: "Write a sample topic and see a caption generated in this client's saved voice, so you can sanity-check the rules before they're used on real posts.",
  },
  {
    key: "social-client-health-card",
    title: "Client Health",
    body: "A quick snapshot of how this client's social presence is doing — recent activity, any gaps in the next week's schedule, and anything that needs attention.",
  },
  {
    key: "social-clients-toolbar",
    title: "Searching Clients",
    body: "Search by client name or filter the list down to clients whose health needs a closer look.",
  },
  {
    key: "social-clients-add",
    title: "Adding a Client",
    body: "Create a new client with just a name — add its colour, logo, timezone, booking link and phone now or later. Then connect its social accounts, or assign accounts you've already connected, on the Accounts page.",
  },
  {
    key: "social-client-edit",
    title: "Editing a Client",
    body: "Change this client's name, colour, logo, timezone, booking link or phone, or archive the client.",
  },
  {
    key: "social-client-edit-dialog",
    title: "Client Details",
    body: "The timezone is used when you schedule posts for this client. The booking link and phone fill the {{booking_url}} and {{phone}} variables in your snippets. Archiving hides the client everywhere but keeps its posts and accounts.",
  },
  {
    key: "social-clients-archived",
    title: "Archived Clients",
    body: "Clients you've archived. Restore one to bring it back to the client switcher, overview and attention list with its posts and accounts intact.",
  },
  {
    key: "social-client-switcher",
    title: "Switching Clients",
    body: "Changes which client's accounts, posts, and settings the rest of the app shows. Choose “All clients” to see everything at the agency level.",
  },
  {
    key: "social-knowledge-scope",
    title: "Knowledge Scope",
    body: "Shows whether you're viewing knowledge for one client plus org-wide knowledge, or everything across every client. New knowledge you add is saved to whichever scope is currently selected.",
  },
  {
    key: "social-knowledge-ask",
    title: "Ask the Knowledge Base",
    body: "Test what the AI copywriter can find for a question, exactly as it would when writing a caption or chat reply — useful for checking your uploaded documents actually answer common questions.",
  },
  {
    key: "social-knowledge-upload",
    title: "Upload Document",
    body: "Add a PDF, Word doc, plain text file, or a public web page to the knowledge base. The AI copywriter uses it to answer questions and write more accurate, on-topic captions.",
  },
  {
    key: "social-brand-engagement-automation",
    title: "Engagement Automation",
    body: "Controls whether the engagement engine drafts or sends comment and DM replies for this client, and whether it generates suggestions for the outbound engagement queue. Draft mode writes a reply for a human to approve in the Inbox's Automation tab; Auto-send posts within the guardrails here without a tap.",
    bullets: [
      "A comment matching an escalation keyword always goes to a human, in every mode.",
      "An automated DM's first message in a conversation always discloses it's automated — required by law.",
    ],
  },
  {
    key: "social-brand-video-style",
    title: "Video Style",
    body: "The default look for this client's reels — captions, pacing and branding. Quick Reel and Video Autopilot use these settings whenever a render doesn't choose its own.",
    bullets: ["Changes apply to new renders only; reels already made keep the style they were rendered with."],
  },
  {
    key: "social-brand-private-reply",
    title: "Private DM Reply",
    body: "When a public comment contains one of your keywords, the assistant drafts one private DM reply to the commenter using your template. Meta allows a single private reply per comment, within 7 days of it being posted.",
    bullets: [
      "Nothing is sent automatically — each draft waits in the Inbox AI drafts panel until someone taps Approve.",
      "Keep the template short and personal; it is the commenter's first DM from this account.",
    ],
  },
  {
    key: "social-brand-spam-shield",
    title: "Spam Shield",
    body: 'Hides obvious scam comments on this client\'s Facebook and Instagram posts within one sync (about 15 minutes) — for example the "my shy friend, add her on snap" comments. Only clear scam patterns are hidden automatically; anything less certain is tagged Spam in the inbox for a person to decide.',
    bullets: [
      "Hidden, never deleted: the commenter and their friends still see it, so they don't just post it again.",
      'Every hide shows in the comment\'s activity as "Spam shield", and you can unhide it from the inbox.',
      "A comment a teammate has already hidden or unhidden is never touched again.",
    ],
  },
];
